/**
 * Put the Samsung platform inventory somewhere the fleet can read it, without
 * anyone standing in front of the television.
 *
 * WHY THIS EXISTS. The facts that unblock the remaining Tizen tickets - the
 * real `tizenId` of every installed app (TIZEN-14), the actual method names on
 * `webapis.systemcontrol` (TIZEN-13), which keys `registerKey` accepted
 * (TIZEN-15) - all lived on a diagnostic page reachable only by typing a URL.
 * The Samsung widget has no address bar, so reading them meant a person, a
 * panel, and a photograph. Two attempts to add a remote-key shortcut both
 * failed on the same rock: the keys we reached for were not being delivered by
 * this remote.
 *
 * The console ring buffer was already the answer and nobody had used it. It is
 * uploaded by the existing `log_capture` command (see logBuffer.ts and
 * diagnostics.ts), which the CMS can fire at any device from a desk. So the app
 * simply SAYS all of this at boot, and the inventory arrives in the fleet
 * console on request - no chord, no camera, no trip to the bench.
 *
 * It is written as ordinary console output rather than new telemetry fields on
 * purpose: this is discovery, and the shape of what we want to know changes
 * every time the panel answers. A schema would have to be migrated each time;
 * a log line does not. Once a fact here turns out to matter permanently, it
 * graduates into the heartbeat as a real field.
 *
 * Nothing here may throw, and nothing here may print a secret: the buffer is
 * uploaded to the fleet console and kept.
 */

import { tizenKeyClaimReport } from "./tizenKeys";
import { offlineShellStatus } from "./offlineShell";

const TAG = "[tizen-inventory]";

/** Run a read that may not exist, may throw, or may do both. */
function safe(fn: () => unknown): string {
  try {
    const v = fn();
    if (v === undefined || v === null || v === "") return "—";
    return String(v);
  } catch (err) {
    return `!! ${err instanceof Error ? err.message : "threw"}`;
  }
}

/** The own+prototype keys of a platform namespace (Samsung's are prototype-backed). */
function methodsOf(obj: unknown): string[] {
  try {
    if (!obj || typeof obj !== "object") return [];
    const out = new Set<string>();
    for (const k of Object.keys(obj as object)) out.add(k);
    const proto = Object.getPrototypeOf(obj as object);
    if (proto && proto !== Object.prototype) {
      for (const k of Object.getOwnPropertyNames(proto)) {
        if (k !== "constructor") out.add(k);
      }
    }
    return Array.from(out).sort();
  } catch {
    return [];
  }
}

interface TizenWindow {
  tizen?: {
    application?: {
      getAppsInfo?: (
        ok: (list: Array<{ id?: string; name?: string }>) => void,
        err: (e: unknown) => void
      ) => void;
    };
  };
  webapis?: Record<string, unknown>;
  b2bapis?: Record<string, unknown>;
}

/**
 * Apps per log line. MEASURED 2026-10-08 on an HU8000F (Tizen 9): 454
 * installed apps, one line each, overflowed the 500-line ring buffer and
 * pushed the namespace lines - the ones that answer "can this panel flash
 * firmware / clone" - out before a capture could read them. Batching keeps the
 * harvest to ~20 lines.
 */
const APPS_PER_LINE = 25;

/**
 * The namespaces, and what is actually callable on each. Logged at boot AND
 * again once the (asynchronous, long) app harvest has printed, so the lines
 * that matter most are always the newest in the buffer.
 */
function logNamespaces(w: TizenWindow, when?: string): void {
  try {
    const tag = when ? `${TAG} (${when})` : TAG;
    const webapis = w.webapis;
    console.info(`${tag} webapis.*=${methodsOf(webapis).join(" ") || "—"}`);
    // systemcontrol: reboot/capture/updateFirmware. clone + tvinfo: the
    // undocumented hospitality namespaces a shipping SI widget calls (cloning,
    // Hotel Option writes) - "absent" here means absent AT webapis.<ns>, which
    // the webapis.* line above confirms or contradicts.
    for (const ns of [
      "systemcontrol",
      "productinfo",
      "network",
      "clone",
      "tvinfo",
      "was",
    ]) {
      const obj = (webapis as Record<string, unknown> | undefined)?.[ns];
      console.info(
        `${tag} webapis.${ns}.*=${methodsOf(obj).join(" ") || "absent"}`
      );
    }
    // Hospitality-only surface: every b2b capability in foyerBridge.ts is
    // gated on this namespace. "absent" on a hospitality panel says our
    // config.xml/cert does not grant it.
    console.info(
      `${tag} b2bapis.*=${methodsOf(w.b2bapis).join(" ") || "absent"}`
    );
    console.info(
      `${tag} b2bapis.b2bcontrol.*=${
        methodsOf(w.b2bapis?.b2bcontrol).join(" ") || "absent"
      }`
    );
  } catch {
    // Discovery must never be the reason a television fails to start.
  }
}

/** Short, single-line rendering of whatever a platform call returned. */
function brief(v: unknown): string {
  try {
    if (v === undefined) return "undefined";
    const t = typeof v === "string" ? v : JSON.stringify(v);
    return t.length > 300 ? `${t.slice(0, 300)}…` : t;
  } catch {
    return String(v);
  }
}

/**
 * PRESENCE IS NOT PERMISSION. A namespace method being enumerable says nothing
 * about whether OUR certificate may call it - Samsung enforces partner
 * privileges at call time with a SecurityError. So, for the TEP firmware +
 * cloning work, actually call the members that are read-only or reversible
 * and log the verdict:
 *
 *  - getVersion() on each namespace (cheapest call through the gate);
 *  - set/unset of the firmware-update and clone-status LISTENERS - the same
 *    privilege gate as updateFirmware()/restoreCloneData(), with no side
 *    effect: registering a callback flashes nothing and restores nothing;
 *  - systemcontrol.getCustomAppInfo() (the TEP server URL, read only);
 *  - the tvinfo.TvInfoMenuKey table, i.e. which Hotel Option settings the
 *    app could read/write, plus one getMenuValue() read.
 *
 * Nothing here writes a setting, starts a download or restores clone data.
 */
function logPermissionProbe(w: TizenWindow): void {
  const webapis = (w.webapis ?? {}) as Record<string, Record<string, unknown>>;
  const call = (label: string, fn: () => unknown) => {
    try {
      console.info(`${TAG} perm ${label}=ok ${brief(fn())}`);
    } catch (err) {
      const e = err as { name?: string; message?: string } | undefined;
      console.info(
        `${TAG} perm ${label}=DENIED ${e?.name ?? "Error"}: ${e?.message ?? String(err)}`
      );
    }
  };
  const fnOf = (ns: string, m: string) => {
    const o = webapis[ns];
    const f = o?.[m];
    return typeof f === "function"
      ? (...a: unknown[]) => (f as (...x: unknown[]) => unknown).apply(o, a)
      : null;
  };
  const noop = () => {};

  for (const ns of ["systemcontrol", "clone", "tvinfo", "was"]) {
    const v = fnOf(ns, "getVersion");
    if (v) call(`${ns}.getVersion`, () => v());
  }

  const getCustom = fnOf("systemcontrol", "getCustomAppInfo");
  if (getCustom) call("systemcontrol.getCustomAppInfo", () => getCustom());

  const setFw = fnOf("systemcontrol", "setUpdateFirmwareListener");
  const unsetFw = fnOf("systemcontrol", "unsetUpdateFirmwareListener");
  if (setFw) {
    call("systemcontrol.setUpdateFirmwareListener", () => {
      const id = setFw(noop);
      if (unsetFw) {
        try {
          unsetFw(id);
        } catch {
          /* the set verdict is what we are after */
        }
      }
      return id;
    });
  }

  const setClone = fnOf("clone", "setCloneUpdateStatusListener");
  const unsetClone = fnOf("clone", "unSetCloneUpdateStatusListener");
  if (setClone) {
    call("clone.setCloneUpdateStatusListener", () => {
      const id = setClone(noop);
      if (unsetClone) {
        try {
          unsetClone(id);
        } catch {
          /* see above */
        }
      }
      return id;
    });
  }

  // The argument lists Samsung never documented. webapis.js is plain JS, so a
  // wrapper's own source names its parameters and shows the validation it
  // runs - the cheapest way to learn restoreCloneData's signature before
  // calling it for real. Truncated; read-only.
  for (const [ns, m] of [
    ["clone", "restoreCloneData"],
    ["clone", "setCloneUpdateStatusListener"],
    ["systemcontrol", "updateFirmware"],
    ["systemcontrol", "setUpdateFirmwareListener"],
    ["systemcontrol", "setCustomAppInfo"],
  ] as const) {
    try {
      const f = webapis[ns]?.[m];
      if (typeof f !== "function") continue;
      const src = Function.prototype.toString
        .call(f)
        .replace(/\s+/g, " ")
        .slice(0, 700);
      console.info(
        `${TAG} src ${ns}.${m}(${(f as { length: number }).length})=${src}`
      );
    } catch {
      // A native function that refuses toString tells us nothing; move on.
    }
  }

  const menuKeys = webapis.tvinfo?.TvInfoMenuKey;
  if (menuKeys && typeof menuKeys === "object") {
    const names = Object.keys(menuKeys as object).sort();
    console.info(`${TAG} perm tvinfo.TvInfoMenuKey=${names.join(" ") || "—"}`);
    const getMenu = fnOf("tvinfo", "getMenuValue");
    const first = names[0];
    if (getMenu && first) {
      call(`tvinfo.getMenuValue(${first})`, () =>
        getMenu((menuKeys as Record<string, unknown>)[first])
      );
    }
  }
}

/**
 * Log the inventory once. Safe on every platform - off Tizen there is no
 * `webapis` and it logs a single line saying so, which is itself the answer
 * when a panel we believed was running the signed widget turns out not to be.
 */
export function logTizenInventory(): void {
  try {
    if (typeof window === "undefined") return;
    const w = window as unknown as TizenWindow;
    if (!w.tizen && !w.webapis) return; // not a Samsung panel; say nothing

    console.info(`${TAG} userAgent=${safe(() => navigator.userAgent)}`);
    // Whether this panel has an offline shell at all. Registration happens
    // asynchronously, so at boot this usually reads "not attempted" - the value
    // that matters is the one a LATER capture shows, and on a widget we expect
    // a cross-origin scope failure (TIZEN-12).
    console.info(`${TAG} offlineShell=${safe(() => offlineShellStatus())}`);
    console.info(`${TAG} registerKey=${safe(() => tizenKeyClaimReport())}`);

    logNamespaces(w);

    // The installed-app harvest. Callback-style (pre-Promise Tizen API), and
    // `application.info` is declared in config.xml. Batched APPS_PER_LINE to a
    // line so a 450-app panel cannot flood the ring buffer.
    const app = w.tizen?.application;
    if (typeof app?.getAppsInfo !== "function") {
      console.info(`${TAG} apps=getAppsInfo absent`);
      logPermissionProbe(w);
      return;
    }
    app.getAppsInfo(
      (list) => {
        try {
          const rows = Array.isArray(list) ? list : [];
          console.info(`${TAG} apps=${rows.length} installed`);
          for (let i = 0; i < rows.length; i += APPS_PER_LINE) {
            const chunk = rows
              .slice(i, i + APPS_PER_LINE)
              .map((a) => `${a?.name ?? "?"} = ${a?.id ?? "?"}`);
            console.info(`${TAG} app ${chunk.join(" | ")}`);
          }
        } catch (e) {
          console.info(`${TAG} apps=render threw ${String(e)}`);
        }
        logNamespaces(w, "after apps");
        logPermissionProbe(w);
      },
      (e) => {
        console.info(
          `${TAG} apps=denied/threw ${e instanceof Error ? e.message : String(e)}`
        );
        logNamespaces(w, "after apps");
        logPermissionProbe(w);
      }
    );
  } catch {
    // Discovery must never be the reason a television fails to start.
  }
}
